"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectorMapMethod = void 0;
/**
 * To use this hook, the component using it must have access to a Provider whose redux store
 * complies with the ValidReduxState type.
 */
var react_redux_1 = require("react-redux");
var react_1 = require("react");
var lodash_1 = require("lodash");
var slice_1 = require("../../slices/carPolicies/slice");
var carPolicies_1 = require("../../functions/carPolicies");
var actions_1 = require("../../slices/carPolicies/actions");
/** Used external hooks */
var useResettableNetworkStatus_1 = __importDefault(require("../useResettableNetworkStatus"));
/** Local functions */
var getMostRecentPolicySource_1 = __importDefault(require("../useAlreadyInsuredCarCheck/getMostRecentPolicySource"));
/** How long to wait before firing the next check request */
var DEBOUNCE_TIMEOUT_MS = 1800;
/**
 * Allows us to extract the required data from the redux store.
 * This is why, in order to use this hook in your project, the rendering component must be located
 * inside a redux provider which serves a state that conforms to `ValidReduxState`. See the
 * slices/carPolicies section.
 *
 * @param state - Reference to a valid redux state
 */
exports.selectorMapMethod = function (state) { return state.carPolicies; };
function useAlreadyInsuredNewCarCheck(_a) {
    var vinNumberValidationError = _a.vinNumberValidationError, motorNumberValidationError = _a.motorNumberValidationError, onMostRecentPolicyFound = _a.onMostRecentPolicyFound, client = _a.client, vinNumber = _a.vinNumber, motorNumber = _a.motorNumber, onSerialMotorMustReset = _a.onSerialMotorMustReset;
    /** True if one of the request is running */
    var _b = react_1.useState(false), updatingData = _b[0], toggleUpdatingData = _b[1];
    /**
     * To prevent logic from executing as soon as the screen renders, we keep track on whether this
     * call corresponds to the first render, and block certain actions accordingly
     */
    var _c = react_1.useState(true), isFirstRender = _c[0], toggleIsFirstRender = _c[1];
    react_1.useEffect(function () {
        toggleIsFirstRender(false);
    }, []);
    /**
     * To perform redux transactions without defining actions, we need a reference to
     * the dispatch object directly.
     */
    var dispatch = react_redux_1.useDispatch();
    /**
     * Extract the policy list from the store.
     */
    var _d = react_redux_1.useSelector(exports.selectorMapMethod), carPolicyForVin = _d.carPolicyForVin, carPolicyForMotor = _d.carPolicyForMotor, mostRecentCarPolicy = _d.mostRecentCarPolicy, policiesForVinRequestStatus = _d.policiesForVinRequestStatus, policiesForMotorRequestStatus = _d.policiesForMotorRequestStatus, reportFoundPolicyData = _d.reportFoundPolicyData;
    /**
     * This is the variable that will be used to check if there is an active policy for either
     * the license plate, the vin number, or motor number
     */
    var hasActivePolicy = react_1.useMemo(function () { return carPolicies_1.isActive(carPolicyForVin) || carPolicies_1.isActive(carPolicyForMotor); }, [carPolicyForVin, carPolicyForMotor]);
    var localPoliciesForVinRequestStatus = useResettableNetworkStatus_1.default(policiesForVinRequestStatus);
    react_1.useEffect(function () {
        if (localPoliciesForVinRequestStatus === 'success' &&
            !isFirstRender &&
            onSerialMotorMustReset) {
            onSerialMotorMustReset();
            dispatch(actions_1.resetMotorPolicy());
        }
    }, [localPoliciesForVinRequestStatus, vinNumber]);
    /**
     * Notify when a "most recent" car policy is found, which can come from either the  vin, or motor
     * check requests.
     */
    react_1.useEffect(function () {
        if (!isFirstRender &&
            typeof mostRecentCarPolicy !== 'undefined' &&
            !hasActivePolicy &&
            ![policiesForVinRequestStatus, policiesForMotorRequestStatus].includes('requesting') &&
            reportFoundPolicyData) {
            onMostRecentPolicyFound(mostRecentCarPolicy);
            return function () {
                dispatch(slice_1.updateReportPolicyData(false));
            };
        }
        return function () {
            // empty function
        };
    }, [
        mostRecentCarPolicy,
        hasActivePolicy,
        policiesForVinRequestStatus,
        policiesForMotorRequestStatus,
        reportFoundPolicyData,
    ]);
    var shouldDisableMotor = react_1.useMemo(function () { return carPolicies_1.isActive(carPolicyForVin); }, [
        carPolicyForVin,
    ]);
    /**
     * Fires the vin checking request
     */
    var debouncedVinCheck = react_1.useRef(lodash_1.debounce(function (dVinNumber, errorVin) {
        if (!errorVin && dVinNumber !== '') {
            dispatch(actions_1.requestExistingPoliciesForVin(dVinNumber, client, true, false));
        }
    }, DEBOUNCE_TIMEOUT_MS)).current;
    /**
     * Fires the motor checking request
     */
    var debouncedMotorCheck = react_1.useRef(lodash_1.debounce(function (dMotorNumber, errorMotor) {
        if (!errorMotor && dMotorNumber !== '') {
            dispatch(actions_1.requestExistingPoliciesForMotor(dMotorNumber, client));
        }
    }, DEBOUNCE_TIMEOUT_MS)).current;
    /**
     * Hook that fires the next vin check if conditions are met, or resets the vin validation logic if the
     * data is empty.
     */
    react_1.useEffect(function () {
        /** If it detects an error, cancel any pending calls to the debounced request */
        if (vinNumberValidationError) {
            debouncedVinCheck.cancel();
            return;
        }
        if (!isFirstRender) {
            toggleUpdatingData(true);
            debouncedVinCheck(vinNumber, vinNumberValidationError);
        }
        if (vinNumber === '') {
            dispatch(actions_1.resetVinPolicy());
        }
    }, [vinNumber, vinNumberValidationError]);
    /**
     * Hook that fires the next motor check if conditions are met, or resets the motor validation logic if the
     * data is empty.
     */
    react_1.useEffect(function () {
        /** If it detects an error, cancel any pending calls to the debounced request */
        if (motorNumberValidationError) {
            debouncedMotorCheck.cancel();
            return;
        }
        /** Only check for vin if these conditions are met */
        if (!carPolicies_1.isActive(carPolicyForVin) && !isFirstRender) {
            toggleUpdatingData(true);
            debouncedMotorCheck(motorNumber, motorNumberValidationError);
        }
        if (motorNumber === '') {
            dispatch(actions_1.resetMotorPolicy());
        }
    }, [motorNumber, motorNumberValidationError]);
    /**
     * Sets updating data to "false" when the request finishes
     */
    react_1.useEffect(function () {
        if (policiesForVinRequestStatus === 'success') {
            toggleUpdatingData(false);
        }
    }, [policiesForVinRequestStatus]);
    react_1.useEffect(function () {
        if (policiesForMotorRequestStatus === 'success') {
            toggleUpdatingData(false);
        }
    }, [policiesForMotorRequestStatus]);
    /**
     * To detect if the last active policy came from the vin or the motor request, we keep track
     * here of the original soruce of the `mostRecentCarPolicy` variable via an union type, which can be
     * either 'vinNumber', 'motorNUmber', or '', depending on this useMemo's internal logic.
     */
    var mostRecentPolicySource = react_1.useMemo(function () {
        return getMostRecentPolicySource_1.default({
            carPolicyForLicense: undefined,
            carPolicyForVin: carPolicyForVin,
            carPolicyForMotor: carPolicyForMotor,
            mostRecentCarPolicy: mostRecentCarPolicy,
        });
    }, [carPolicyForVin, carPolicyForMotor, mostRecentCarPolicy]);
    return {
        hasActivePolicy: hasActivePolicy,
        mostRecentCarPolicy: mostRecentCarPolicy,
        vinRequestStatus: policiesForVinRequestStatus,
        motorRequestStatus: policiesForMotorRequestStatus,
        shouldDisableMotor: shouldDisableMotor,
        updatingData: updatingData,
        mostRecentPolicySource: mostRecentPolicySource,
    };
}
exports.default = useAlreadyInsuredNewCarCheck;
//# sourceMappingURL=useAlreadyInsuredNewCarCheck.js.map